import { Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { LogOut } from "lucide-react";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { SettingsMenuItem } from "web_myplann/More/Components/SettingsMenuItem";
import { useSettingsMenuItems } from "web_myplann/More/Hooks/useSettingsMenuItems";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { appInfoState } from "web_myplann/Shared/State/appInfoAtom";
import {
	BLOOD_RED_MYPLANN,
	SETTINGS_LOGOUT_ICON_BG,
} from "web_myplann/Utils/constants";
import { device } from "web_myplann/Utils/device";

export const MorePage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const queryClient = useQueryClient();
	const appInfoAtom = useAtomValue(appInfoState);
	const { toolItems, settingsItems, supportItems } = useSettingsMenuItems();

	const handleLogOut = async () => {
		if (window.ReactNativeWebView) {
			await device.logOut();
		}
		queryClient.clear();
	};

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={theme.palette.grey[100]}
			topBar={
				<NavigationHeader
					title={t(Resources.Settings.title)}
					defaultRoute={Routing.Dashboard.path()}
				/>
			}
		>
			<Stack sx={{ minHeight: "100%" }}>
				<CollapsibleSection title={t(Resources.Settings.Sections.Tools)}>
					<Stack sx={{ bgcolor: theme.palette.background.paper }}>
						{toolItems.map((item, index) => (
							<SettingsMenuItem
								key={item.label}
								item={item}
								showDivider={index < toolItems.length - 1}
							/>
						))}
					</Stack>
				</CollapsibleSection>

				<CollapsibleSection title={t(Resources.Settings.Sections.Settings)}>
					<Stack sx={{ bgcolor: theme.palette.background.paper }}>
						{settingsItems.map((item, index) => (
							<SettingsMenuItem
								key={item.label}
								item={item}
								showDivider={index < settingsItems.length - 1}
							/>
						))}
					</Stack>
				</CollapsibleSection>

				<CollapsibleSection title={t(Resources.Settings.Sections.Support)}>
					<Stack sx={{ bgcolor: theme.palette.background.paper }}>
						{supportItems.map((item, index) => (
							<SettingsMenuItem
								key={item.label}
								item={item}
								showDivider={index < supportItems.length - 1}
							/>
						))}
					</Stack>
				</CollapsibleSection>

				<Stack sx={{ bgcolor: theme.palette.background.paper, mt: 2 }}>
					<Stack
						direction="row"
						alignItems="center"
						gap={1.6}
						px={1.6}
						py={1.2}
						onClick={handleLogOut}
						sx={{
							cursor: "pointer",
							"&:active": { bgcolor: theme.palette.grey[50] },
						}}
					>
						<IconBox bg={SETTINGS_LOGOUT_ICON_BG} shape="circle">
							<LogOut size={20} color={BLOOD_RED_MYPLANN} />
						</IconBox>
						<Typography variant="subtitle1" color={BLOOD_RED_MYPLANN} flex={1}>
							{t(Resources.Settings.LogOut)}
						</Typography>
					</Stack>
				</Stack>

				<Stack alignItems="center" py={3}>
					<Typography variant="hint">
						{`myPLANN ${appInfoAtom.appVersion}`}
					</Typography>
					<Typography variant="hint" mt={0.5}>
						{t(Resources.Settings.Copyright)}
					</Typography>
				</Stack>
			</Stack>
		</MyPlannLayout>
	);
};
